import { Injectable } from "@angular/core";
import { DataService } from "./data.service";
import { environment } from "src/environments/environment";
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from "rxjs";
import { PatientDoc, UploadPatientDoc } from "../models/patient-docs.model";
import {
  ApplicationCacheService,
  ApplicationCacheWriteTarget,
} from "./application-cache.service";
import { ApiStatus, PromptType, ReferralApis, UploadDocumentType, Utils } from "./utils";
import { cloneDeep } from "lodash";
import {
  isPlanOrderDocumentTypeLabel,
  isPlanOrderSignableType,
  isProcedureRoomProtocolSignableType,
  isSoapNoteSignableType,
  parsePatientHeaderFromHtml,
  PROCEDURE_ROOM_PROTOCOL_DOCUMENT_TYPE_DISPLAY,
  resolveSignableDocumentDos,
  resolveSoapNoteSignableDisplayTitle,
  SOAP_NOTE_DOCUMENT_TYPE_DISPLAY,
} from "../helpers/signable-document-header.util";
import { formatPatientDisplayName } from "../helpers/phi-encounter-patient.util";
import { Appointment } from "../models/admissions.model";
import { ApplicationContextService } from "./application-context.service";
import { HpiFindings, HpiSummary } from "../models/hpi-findings";
import { Medication } from "../models/procedure.model";
import { Allergy } from "../models/allergy.model";
@Injectable({
  providedIn: "root",
})
export class PatientDocsService {
  constructor(
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _appContextService: ApplicationContextService
  ) {}

  private deleteSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(
    null
  );

  idToBeDeleted$: Observable<string> = this.deleteSubject$.asObservable();

  savePatientDoc(payload: UploadPatientDoc): Observable<PatientDoc> {
    // this is an extra endpoint because api/v1/documents does not return the inserted record
    //if case we need to return the inserted record we can use this endpoint
    //if you want all patient records returned, use the api/v1/documents api
    const url = this.apiBaseUrl + `api/v1/documents/add`;
    return this._dataService.post(url, payload);
  }

  getEncounterDocs(encounterId: string): Observable<PatientDoc[]> {
    // Check if we already have documents for this specific encounter
    const currentAppointmentId = this._applicationCacheService.applicationCache.appointment?.id;
    if (this.isEncounterDocsAlreadyLoaded && currentAppointmentId === encounterId) {
      return of(this.documentCache);
    }
    
    const url = this.apiBaseUrl + `api/v1/documents/encounter/${encounterId}`;
    return this._dataService.get(url).pipe(
      tap((patientDocs: PatientDoc[]) => {
        const docs = patientDocs.map((doc) => ({
          ...doc,
          isProcedureDocument: false, // phi currently does not supports procedural docs.
        }));
        this._applicationCacheService.setDocumentCache(docs);
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.Documents
        );
      }),
      catchError((error) => {
this._applicationCacheService.setApiStatus(
          ApiStatus.Failed,
          ReferralApis.Documents
        );
        return throwError(() => error);
      })
    );
  }

  /**
   * Get encounter documents with carry-over of Identification, InsuranceInfo, and SecondaryInsurance from previous encounters.
   * This method fetches encounter-specific documents and also includes patient-level
   * documents of those types from previous encounters if they don't exist in the current encounter.
   * @param encounterId The encounter ID
   * @returns Observable<PatientDoc[]> with merged documents
   */
  getEncounterDocsWithCarryOver(
    encounterId: string,
    options?: { writeTarget?: ApplicationCacheWriteTarget },
  ): Observable<PatientDoc[]> {
    const readCache =
      options?.writeTarget === "preview" &&
      this._applicationCacheService.isSearchPreviewOverlayActive
        ? this._applicationCacheService.searchPreviewCache
        : this._applicationCacheService.applicationCache;
    const currentAppointmentId = readCache.appointment?.id;
    const forceRefresh = options?.writeTarget === "preview";
    if (
      !forceRefresh &&
      this.isEncounterDocsAlreadyLoaded &&
      currentAppointmentId === encounterId
    ) {
      return of(this.documentCache);
    }

    // Get patientId from cache to fetch patient-level documents
    const patientId = readCache.patient?.id;
    
    // Document types that should carry over from previous encounters
    const carryOverDocumentTypes = [
      UploadDocumentType.Identification,
      UploadDocumentType.InsuranceInfo,
      UploadDocumentType.SecondaryInsurance
    ];
    
    // Fetch encounter-specific documents
    const encounterDocsUrl = this.apiBaseUrl + `api/v1/documents/encounter/${encounterId}`;
    const encounterDocs$ = this._dataService.get(encounterDocsUrl);
    
    // Fetch patient-level documents if patientId is available
    const patientDocs$ = patientId 
      ? this._dataService.get(this.apiBaseUrl + `api/v1/documents/patient/${patientId}/all`).pipe(
          map((patientDocs: PatientDoc[]) => {
            // Filter to only get Identification, InsuranceInfo, and Secondary Insurance documents
            // and exclude documents that already exist in this encounter
            return patientDocs.filter((doc) => 
              carryOverDocumentTypes.includes(doc.documentType as UploadDocumentType) &&
              doc.encounterId !== encounterId // Exclude documents from current encounter
            );
          }),
          catchError((error) => {
            // If patient docs fetch fails, just return empty array
            console.warn('Failed to fetch patient-level documents:', error);
            return of([]);
          })
        )
      : of([]);
    
    // Use forkJoin to fetch both in parallel
    return forkJoin({
      encounterDocs: encounterDocs$,
      patientDocs: patientDocs$
    }).pipe(
      map(({ encounterDocs, patientDocs }: { encounterDocs: PatientDoc[]; patientDocs: PatientDoc[] }) => {
        // Create a map of encounter docs by documentType for quick lookup
        const encounterDocsByType = new Map<string, PatientDoc>();
        encounterDocs.forEach((doc) => {
          encounterDocsByType.set(doc.documentType, doc);
        });
        
        // Merge documents: prioritize encounter docs, but include patient docs if encounter doesn't have them
        const mergedDocs: PatientDoc[] = [...encounterDocs];
        
        // Add patient-level docs only if encounter doesn't already have that document type
        patientDocs.forEach((patientDoc) => {
          if (!encounterDocsByType.has(patientDoc.documentType)) {
            // Update the encounterId to current encounter so it appears in this encounter's context
            mergedDocs.push({
              ...patientDoc,
              encounterId: encounterId,
              isProcedureDocument: false
            });
          }
        });
        
        return mergedDocs;
      }),
      tap((patientDocs: PatientDoc[]) => {
        const docs = patientDocs.map((doc) => ({
          ...doc,
          isProcedureDocument: false, // phi currently does not supports procedural docs.
        }));
        this._applicationCacheService.setDocumentCache(docs, options);
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.Documents
        );
      }),
      catchError((error) => {
this._applicationCacheService.setApiStatus(
          ApiStatus.Failed,
          ReferralApis.Documents
        );
        return throwError(() => error);
      })
    );
  }

  getEncounterDocsAll(encounterId: string): Observable<PatientDoc[]> {
    const url = this.apiBaseUrl + `api/v1/documents/encounter/${encounterId}/all`;
    return this._dataService.get(url).pipe(
      tap((patientDocs: PatientDoc[]) => {
        const docs = patientDocs.map((doc) => ({
          ...doc,
          isProcedureDocument: false, // phi currently does not supports procedural docs.
        }));
        this._applicationCacheService.setDocumentCache(docs);
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.Documents
        );
      }),
      catchError((error) => {
this._applicationCacheService.setApiStatus(
          ApiStatus.Failed,
          ReferralApis.Documents
        );
        return throwError(() => error);
      })
    );
  }

  getPatientDocsAll(patientId: string): Observable<PatientDoc[]> {
    const url = this.apiBaseUrl + `api/v1/documents/patient/${patientId}/all`;
    return this._dataService.get(url).pipe(
      tap((patientDocs: PatientDoc[]) => {
        const docs = patientDocs.map((doc) => ({
          ...doc,
          isProcedureDocument: false, // phi currently does not supports procedural docs.
        }));
        this._applicationCacheService.setDocumentCache(docs);
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.Documents
        );
      }),
      catchError((error) => {
this._applicationCacheService.setApiStatus(
          ApiStatus.Failed,
          ReferralApis.Documents
        );
        return throwError(() => error);
      })
    );
  }

  /**
   * Get signables with documents for an encounter
   * Uses the new API endpoint: GET /api/encounters/{id}/signables-with-documents
   * @param encounterId The encounter ID
   * @returns Observable<PatientDoc[]> with transformed documents
   */
  getEncounterSignablesWithDocuments(
    encounterId: string,
    skipCacheUpdate: boolean = false,
    encounterAppointment?: Appointment | null
  ): Observable<PatientDoc[]> {
    const url = this.apiBaseUrl + `api/encounters/${encounterId}/signables-with-documents`;
    return this._appContextService.context.pipe(
      take(1),
      switchMap(() => this._dataService.get(url)),
      map((response: any[]) => {
        // Try to get patientId from application cache for signables that don't have it
        const cacheAppointment =
          this._applicationCacheService.applicationCache.appointment;
        const appointment =
          encounterAppointment?.id === encounterId
            ? encounterAppointment
            : cacheAppointment?.id === encounterId
              ? cacheAppointment
              : null;
        const patientIdFromCache = appointment?.encountersPatient?.id || '';
        const encounterDos =
          appointment?.id === encounterId
            ? this.normalizeDosDate(appointment.encounterDateTime)
            : null;
        
        // Transform both signables and documents into PatientDoc format
        // Documents have: patientId, name, fileName, fileKey
        // Signables have: title, displayName, type, signableType, content, signatures, contextId
        const transformedItems: PatientDoc[] = response.map((item) => {
          // Determine if this is a document or a signable
          const isDocument = !!(item.patientId && item.name && (item.fileName || item.fileKey || item.key));
          const isSignable = !!item.signableType;
          
          // Extract fileKey from fileName if it's a full URL (for documents)
          let fileKey = item.key || item.fileKey;
          if (!fileKey && item.fileName) {
            const urlMatch = item.fileName.match(/s3\.amazonaws\.com\/([^?]+)/);
            if (urlMatch) {
              fileKey = urlMatch[1];
            } else {
              fileKey = item.fileName;
            }
          }
          
          // For signables, use displayName or title as name, and signableType as documentType
          // For documents, use the existing fields
          let name = isDocument ? item.name : (item.displayName || item.title || 'Untitled');
          let documentType = isDocument ? item.documentType : (item.signableType || item.type || 'Signable');
          
          // Use patientId from item if available, otherwise try cache (for signables)
          const patientId = item.patientId || (isSignable ? patientIdFromCache : '');
          
          // For signables, store content in autogeneratedContent and preserve signable data
          const autogeneratedContent = isSignable ? (item.content || null) : (item.autogeneratedContent || null);
          const dos = isSignable
            ? resolveSignableDocumentDos(
                item.scheduleDate,
                autogeneratedContent,
                item.dos || item.createdAt || null
              )
            : item.dos || item.createdAt || null;

          if (isSoapNoteSignableType(item.type, item.signableType)) {
            documentType = SOAP_NOTE_DOCUMENT_TYPE_DISPLAY;
            const soapNoteTitle = this.buildSoapNoteDisplayTitle(
              item,
              appointment,
              autogeneratedContent,
              dos
            );
            if (soapNoteTitle) {
              name = soapNoteTitle;
            }
          } else if (
            isProcedureRoomProtocolSignableType(item.type, item.signableType)
          ) {
            documentType = PROCEDURE_ROOM_PROTOCOL_DOCUMENT_TYPE_DISPLAY;
          } else if (
            isPlanOrderSignableType(item.type, item.signableType) ||
            (!isSignable && isPlanOrderDocumentTypeLabel(item.documentType))
          ) {
            documentType = PROCEDURE_ROOM_PROTOCOL_DOCUMENT_TYPE_DISPLAY;
          }
          
          return {
            id: item.id,
            patientId: patientId,
            name: name,
            fileName: isDocument ? (item.fileName || item.fileKey || item.key || '') : '',
            fileKey: fileKey || '',
            fileLink: item.fileLink || item.fileName || '',
            extension: item.extension || null,
            documentType: documentType,
            documentTypeId: item.documentTypeId || null,
            dos,
            isOld: item.isOld || false,
            isProcedureDocument: item.isProcedureDocument || false,
            status: item.status || null,
            updatedBy: item.updatedBy || null,
            createdAt: item.createdAt || null,
            updatedAt: item.updatedAt || null,
            associatedAuthorizationId: item.associatedAuthorizationId || null,
            encounterId: item.encounterId || encounterId,
            procedureId: item.procedureId || '',
            key: fileKey || '',
            isAutogenerated: item.isAutogenerated || isSignable, // Signables are treated as autogenerated for preview
            autogeneratedContent: autogeneratedContent,
            isPendingUpload: item.isPendingUpload || false,
            contextId: item.contextId || null,
            isContextDocument: item.isContextDocument || isSignable, // Signables are context documents
            patientDocumentId: item.patientDocumentId || null,
            // Store signable metadata for later use (will be accessed via any cast)
            ...(isSignable && {
              signableId: item.id,
              scheduleDate: item.scheduleDate || null,
              signableSignatures: item.signatures || [],
              signableStatus: item.status || 'pending', // Store signable status (fully_signed, partially_signed, pending)
            }),
          } as any as PatientDoc;
        });
        
        const scopedItems = transformedItems.filter((doc) => {
          const docEncounterId = String(doc.encounterId ?? "").trim();
          return !docEncounterId || docEncounterId === encounterId;
        });

        return this.deduplicateEncounterDocuments(
          scopedItems,
          encounterId,
          encounterDos
        );
      }),
      tap((patientDocs: PatientDoc[]) => {
        // Only update global cache if skipCacheUpdate is false
        if (!skipCacheUpdate) {
          const docs = patientDocs.map((doc) => ({
            ...doc,
            isProcedureDocument: false, // phi currently does not supports procedural docs.
          }));
          this._applicationCacheService.setDocumentCache(docs);
          this._applicationCacheService.setApiStatus(
            ApiStatus.Success,
            ReferralApis.Documents
          );
        }
      }),
      catchError((error) => {
this._applicationCacheService.setApiStatus(
          ApiStatus.Failed,
          ReferralApis.Documents
        );
        return throwError(() => error);
      })
    );
  }

  deletePatientDoc(documentId: string): Observable<void> {
    this.deleteSubject$.next(documentId);
    const url = this.apiBaseUrl + `api/v1/documents/${documentId}`;
    return this._dataService
      .delete(url)
      .pipe(finalize(() => this.deleteSubject$.next(null)));
  }

  getPatientDocumentById(documentId: string): Observable<PatientDoc> {
    const id = String(documentId ?? "").trim();
    if (!id) {
      return throwError(() => new Error("Document id is required"));
    }
    const url = `${this.apiBaseUrl}api/v1/documents/${encodeURIComponent(id)}`;
    return this._dataService.get(url);
  }

  /**
   * Resolve a patient document for preview (cache, GET by id, encounter doc lists).
   * Merges rows so `fileKey` from upload/cache is kept when GET-by-id is sparse.
   */
  resolvePatientDocForPreview(
    documentId: string,
    encounterIdCandidates?: string[],
  ): Observable<PatientDoc | null> {
    const id = String(documentId ?? "").trim();
    if (!id) {
      return of(null);
    }
    const cacheDocs = this._applicationCacheService.applicationCache.documents ?? [];
    const cached = cacheDocs.find((row) => row.id === id);
    if (cached?.fileKey?.trim()) {
      return of(cached);
    }

    return this.getPatientDocumentById(id).pipe(
      map((doc) => this.mergePatientDocPreviewRows(cached, doc)),
      catchError(() => of(this.mergePatientDocPreviewRows(cached, null))),
      switchMap((doc) => {
        if (this.hasDocumentStreamFileKey(doc)) {
          return of(doc);
        }
        return this.findPatientDocInEncounterLists(id, encounterIdCandidates).pipe(
          map((fromList) => this.mergePatientDocPreviewRows(doc, fromList) || doc),
        );
      }),
      map((doc) => doc ?? cached ?? null),
      tap((doc) => {
        if (doc?.id) {
          this._applicationCacheService.addDocumentToCache(doc);
        }
      }),
    );
  }

  private hasDocumentStreamFileKey(doc: PatientDoc | null | undefined): boolean {
    if (!doc) {
      return false;
    }
    const row = doc as PatientDoc & { key?: string };
    return !!(row.fileKey?.trim() || row.key?.trim());
  }

  private mergePatientDocPreviewRows(
    primary: PatientDoc | null | undefined,
    secondary: PatientDoc | null | undefined,
  ): PatientDoc | null {
    if (!primary && !secondary) {
      return null;
    }
    if (!primary) {
      return secondary ?? null;
    }
    if (!secondary) {
      return primary;
    }
    const rowA = primary as PatientDoc & { key?: string };
    const rowB = secondary as PatientDoc & { key?: string };
    const fileKey = rowA.fileKey?.trim() || rowB.fileKey?.trim() || "";
    const key = rowA.key?.trim() || rowB.key?.trim() || fileKey;
    return {
      ...secondary,
      ...primary,
      fileKey: fileKey || primary.fileKey,
      ...(key ? { key } : {}),
      fileLink: primary.fileLink?.trim() || secondary.fileLink,
      name: primary.name?.trim() || secondary.name,
      fileName: primary.fileName?.trim() || secondary.fileName,
      extension: primary.extension?.trim() || secondary.extension,
    };
  }

  private previewEncounterIdCandidates(
    encounterIdCandidates?: string[],
  ): string[] {
    const cache = this._applicationCacheService.applicationCache;
    return [
      ...new Set(
        [
          ...(encounterIdCandidates ?? []),
          cache.procedureListing?.encounterId,
          cache.appointment?.id,
        ]
          .map((value) => String(value ?? "").trim())
          .filter(Boolean),
      ),
    ];
  }

  private findPatientDocInEncounterLists(
    documentId: string,
    encounterIdCandidates?: string[],
  ): Observable<PatientDoc | null> {
    const encounterIds = this.previewEncounterIdCandidates(encounterIdCandidates);
    if (!encounterIds.length) {
      return of(null);
    }
    let chain: Observable<PatientDoc | null> = of(null);
    for (const encounterId of encounterIds) {
      chain = chain.pipe(
        switchMap((found) => {
          if (found && this.hasDocumentStreamFileKey(found)) {
            return of(found);
          }
          return this.getEncounterDocs(encounterId).pipe(
            map(
              (docs) =>
                docs.find((row) => row.id === documentId) ??
                found ??
                null,
            ),
            catchError(() => of(found)),
          );
        }),
      );
    }
    return chain;
  }

  /**
   * PATCH /api/v1/documents/{id}/metadata — persist JSON metadata (e.g. OCR results).
   */
  patchDocumentMetadata(
    documentId: string,
    metadata: Record<string, unknown>
  ): Observable<unknown> {
    const id = String(documentId ?? "").trim();
    if (!id) {
      return throwError(() => new Error("Document id is required"));
    }
    const url = `${this.apiBaseUrl}api/v1/documents/${encodeURIComponent(id)}/metadata`;
    return this._dataService.patch(url, { metadata });
  }

  getDocumentLinkByFileName(fileName: string): Observable<string> {
    const key = String(fileName ?? "").trim();
    const url =
      this.apiBaseUrl +
      `api/v1/documents/view?key=${encodeURIComponent(key)}`;
    return this._dataService.get(url, { responseType: "text" });
  }

  /**
   * Load PDF bytes through the authenticated PHI files API (never direct S3 — CORS).
   */
  loadDocumentPreviewSource(
    doc: Partial<PatientDoc> | null | undefined,
  ): Observable<
    { kind: "blob"; blob: Blob } | { kind: "iframeUrl"; url: string }
  > {
    return this.loadDocumentPreviewBlobFallback(doc).pipe(
      catchError(() => this.loadDocumentPreviewIframeUrlFallback(doc)),
    );
  }

  private documentPreviewStreamKeys(
    doc: Partial<PatientDoc> | null | undefined,
  ): string[] {
    const fileKey = this.documentOcrFileKey(doc);
    const streamKey = this.documentStreamKey(doc);
    const documentId = String(doc?.id ?? "").trim();
    return [...new Set([fileKey, streamKey, documentId].filter(Boolean))];
  }

  private loadDocumentPreviewBlobFallback(
    doc: Partial<PatientDoc> | null | undefined,
  ): Observable<{ kind: "blob"; blob: Blob }> {
    const keys = this.documentPreviewStreamKeys(doc);
    if (!keys.length) {
      return throwError(() => new Error("No document stream key"));
    }
    return this.loadDocumentPreviewBlobForKeys(keys, 0);
  }

  private loadDocumentPreviewBlobForKeys(
    keys: string[],
    index: number,
  ): Observable<{ kind: "blob"; blob: Blob }> {
    if (index >= keys.length) {
      return throwError(() => new Error("File not found"));
    }
    return this.getDocumentFileByKey(keys[index]).pipe(
      map((blob) => ({ kind: "blob" as const, blob })),
      catchError(() => this.loadDocumentPreviewBlobForKeys(keys, index + 1)),
    );
  }

  private loadDocumentPreviewIframeUrlFallback(
    doc: Partial<PatientDoc> | null | undefined,
  ): Observable<{ kind: "iframeUrl"; url: string }> {
    const keys = this.documentPreviewStreamKeys(doc);
    if (!keys.length) {
      return throwError(() => new Error("No document stream key"));
    }
    return this.loadDocumentPreviewIframeUrlForKeys(keys, 0);
  }

  private loadDocumentPreviewIframeUrlForKeys(
    keys: string[],
    index: number,
  ): Observable<{ kind: "iframeUrl"; url: string }> {
    if (index >= keys.length) {
      return throwError(() => new Error("View URL not found"));
    }
    return this.getDocumentLinkByFileName(keys[index]).pipe(
      switchMap((viewUrl) => {
        const trimmed = String(viewUrl ?? "").trim();
        if (trimmed && /^https?:\/\//i.test(trimmed)) {
          return of({ kind: "iframeUrl" as const, url: trimmed });
        }
        return throwError(() => new Error("Invalid view URL"));
      }),
      catchError(() => this.loadDocumentPreviewIframeUrlForKeys(keys, index + 1)),
    );
  }

  /**
   * Fetch a document file blob by fileKey
   * @param fileKey The file key to fetch (can be a key or full URL)
   * @returns Observable<Blob> containing the file blob
   */
  getDocumentFileByKey(fileKey: string): Observable<Blob> {
    // Extract fileKey from URL if it's a full URL
    let extractedFileKey = fileKey;
    if (fileKey && /^https?:\/\//i.test(fileKey)) {
      extractedFileKey = Utils.extractFileKeyFromUrl(fileKey) || fileKey;
    }
    
    // Clean the fileKey - remove query parameters and fragments
    if (extractedFileKey) {
      extractedFileKey = extractedFileKey.split('?')[0].split('#')[0];
    }
    
    if (!extractedFileKey || extractedFileKey.trim() === '') {
      return throwError(() => new Error('Invalid fileKey provided'));
    }
    
    const url = this.apiBaseUrl + `api/v1/files/${encodeURIComponent(extractedFileKey.trim())}`;
    // For blob responses, don't set Content-Type header - let the browser handle it
    const blobHeaders = {
      'auth-req': 'true',
      'X-Requested-With': 'XMLHttpRequest'
    };
    return this._dataService.get(url, { 
      responseType: 'blob',
      headers: blobHeaders
    });
  }

  readFileAndFetchDetailsJson(
    fileKeyUrl: string,
    promptType: PromptType
  ): Observable<any> {
    const fileKey = this.resolveOcrFileKey(fileKeyUrl);
    const prompt = Utils.getPromptByType(promptType);

    const url = this.apiUrlForAI + "api/v1/ai/ocr/fileKey";
    return this._dataService.post(url, { fileKey, prompt });
  }

  readFileAndFetchDetailsText(
    fileKeyUrl: string,
    promptType: PromptType
  ): Observable<string> {
    const fileKey = this.resolveOcrFileKey(fileKeyUrl);
    const prompt = Utils.getPromptByType(promptType);

    const url = this.apiUrlForAI + "api/v1/ai/ocr/fileKey";
    return this._dataService.postPlainText(url, { fileKey, prompt });
  }

  fetchHPIResultWithAI(hpiQuery: string): Observable<string> {
    const url = this.apiUrlForAI + "api/v1/ai/hpi";
    return this._dataService.post(url, { hpiQuery });
  }

  generatePreAnesthsiaSummaryUsingAI(
    hpiKey: string,
    abiKey: string,
    ultrasoundKey: string
  ) {
    return forkJoin({
      hpi: this.readFileAndFetchDetailsText(hpiKey, PromptType.HPI),
      abi: abiKey
        ? this.readFileAndFetchDetailsText(abiKey, PromptType.NonInvasive)
        : of(""),
      ultrasound: ultrasoundKey
        ? this.readFileAndFetchDetailsText(ultrasoundKey, PromptType.Ultrasound)
        : of(""),
    }).pipe(
      switchMap(({ hpi, abi, ultrasound }) =>
        forkJoin({
          
        })
      ),
      catchError((error) => {
return throwError(() => error);
      })
    );
  }

  getHpiSummary(procedureId: string): Observable<HpiFindings> {
    const url = this.apiBaseUrl + `api/v1/hpi/procedure/${procedureId}`;
    return this._dataService.get(url);
  }

  private saveMeds(
    procedureId: string,
    patientId: string,
    payload
  ): Observable<Medication[]> {
    const url =
      this.apiBaseUrl +
      `api/v1/medications/map/patient/${patientId}/procedure/${procedureId}`;
    return this._dataService.post(url, payload).pipe(
      tap((response) => {
        this._applicationCacheService.setMedicationReconciliationCache(
          response
        );
      }),
      catchError((error) => {
return throwError(() => error);
      })
    );
  }

  private saveAllergies(
    procedureId: string,
    patientId: string,
    payload
  ): Observable<Allergy[]> {
    const url =
      this.apiBaseUrl +
      `api/v1/allergies/map/patient/${patientId}/procedure/${procedureId}`;
    return this._dataService.post(url, payload).pipe(
      tap((response) => {
        this._applicationCacheService.setAllergiesCache(response);
      }),
      catchError((error) => {
return throwError(() => error);
      })
    );
  }

  saveMedsAndAllergies(
    procedureId: string,
    patientId: string,
    medsAndAllergies
  ): Observable<{
    meds: Medication[];
    allergies: Allergy[];
  }> {
    return forkJoin({
      meds: this.saveMeds(procedureId, patientId, medsAndAllergies),
      allergies: this.saveAllergies(procedureId, patientId, medsAndAllergies),
    });
  }

  get isEncounterDocsAlreadyLoaded(): boolean {
    return (
      this._applicationCacheService.getApiStatus(ReferralApis.Documents) ===
      ApiStatus.Success
    );
  }

  clearDocumentCache(): void {
    this._applicationCacheService.setApiStatus(
      ApiStatus.Pending,
      ReferralApis.Documents
    );
    this._applicationCacheService.applicationCache.documents = [];
  }

  get isPreAnesthsiaSummaryAlreadyLoaded(): boolean {
    return (
      this._applicationCacheService.getApiStatus(
        ReferralApis.PreAnesthsiaSummary
      ) === ApiStatus.Success
    );
  }

  get documentCache(): PatientDoc[] {
    return cloneDeep(this._applicationCacheService.applicationCache.documents);
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  get apiUrlForAI(): string {
    return environment.aiApiUrl;
  }

  private buildSoapNoteDisplayTitle(
    item: Record<string, unknown>,
    appointment: Appointment | null | undefined,
    content: string | null | undefined,
    dos: string | null | undefined
  ): string {
    const parsedHeader = content ? parsePatientHeaderFromHtml(content) : null;
    const patientName =
      formatPatientDisplayName(String(item["patientName"] ?? "")) ||
      formatPatientDisplayName(
        (item["encountersPatient"] as { name?: string } | undefined)?.name
      ) ||
      formatPatientDisplayName(appointment?.encountersPatient?.name) ||
      formatPatientDisplayName(parsedHeader?.name);

    const dosValue =
      dos ||
      appointment?.encounterDateTime ||
      String(item["scheduleDate"] ?? "") ||
      parsedHeader?.dos ||
      null;

    return resolveSoapNoteSignableDisplayTitle({
      title: String(item["title"] ?? ""),
      displayName: String(item["displayName"] ?? ""),
      patientName,
      serviceNameOrId:
        String(item["serviceName"] ?? item["service"] ?? "").trim() || undefined,
      appointmentService: appointment?.service,
      dos: dosValue,
      resolveServiceDisplayName: (serviceNameOrId) =>
        this._appContextService.resolveEncounterServiceDisplayName(
          serviceNameOrId
        ),
    });
  }

  /**
   * Collapse duplicate rows from signables-with-documents.
   * Signable/context protocol rows dedupe by title (same protocol from a prior DOS).
   * Uploaded files still dedupe by title + DOS.
   */
  private deduplicateEncounterDocuments(
    docs: PatientDoc[],
    encounterId: string,
    encounterDos?: string | null
  ): PatientDoc[] {
    if (!docs?.length) {
      return [];
    }

    const groups = new Map<string, PatientDoc[]>();
    for (const doc of docs) {
      const key = this.documentDedupKey(doc);
      const group = groups.get(key) ?? [];
      group.push(doc);
      groups.set(key, group);
    }

    return Array.from(groups.values()).map((group) =>
      this.pickPreferredDocumentRow(group, encounterId, encounterDos)
    );
  }

  private documentDedupKey(doc: PatientDoc): string {
    const name = this.normalizeDocumentNameForDedup(doc.name || "");
    if (this.isSignableOrContextProtocolRow(doc)) {
      return `signable|${name}`;
    }
    const dos = this.normalizeDosDate(doc.dos || doc.createdAt);
    return `file|${name}|${dos}`;
  }

  private isSignableOrContextProtocolRow(doc: PatientDoc): boolean {
    if (this.isSignableDocumentRow(doc)) {
      return true;
    }
    return !!(
      doc.isContextDocument &&
      doc.isAutogenerated &&
      !this.isUploadedDocumentRow(doc)
    );
  }

  private normalizeDosDate(value: string | Date | null | undefined): string {
    if (!value) {
      return "";
    }
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return String(value).trim().slice(0, 10);
    }
    return parsed.toISOString().slice(0, 10);
  }

  private normalizeDocumentNameForDedup(raw: string): string {
    let s = String(raw || "")
      .trim()
      .replace(/\.pdf$/i, "")
      .replace(/\s*\[signable\s+[a-f0-9-]{36}\]\s*$/i, "")
      .trim();
    s = s.replace(/\s*\[signable\s+[a-f0-9-]{36}\]\s*/gi, " ").trim();
    return s.replace(/\s+/g, " ").toLowerCase();
  }

  private isUploadedDocumentRow(doc: PatientDoc): boolean {
    const d = doc as PatientDoc & { key?: string };
    return !!(
      doc.patientId &&
      doc.name &&
      (doc.fileName || doc.fileKey || d.key)
    );
  }

  private isSignableDocumentRow(doc: PatientDoc): boolean {
    const d = doc as PatientDoc & {
      signableId?: string;
      signableStatus?: string;
    };
    return !!(
      d.signableId ||
      (doc.isContextDocument &&
        doc.isAutogenerated &&
        doc.autogeneratedContent &&
        !doc.fileName)
    );
  }

  private pickPreferredDocumentRow(
    group: PatientDoc[],
    encounterId: string,
    encounterDos?: string | null
  ): PatientDoc {
    if (group.length === 1) {
      return group[0];
    }

    const matchingEncounter = group.filter(
      (doc) => String(doc.encounterId ?? "").trim() === encounterId
    );
    if (matchingEncounter.length === 1) {
      return matchingEncounter[0];
    }
    if (matchingEncounter.length > 1) {
      return this.pickPreferredDocumentRowByStatus(matchingEncounter, encounterDos);
    }

    if (encounterDos) {
      const matchingDos = group.filter(
        (doc) => this.normalizeDosDate(doc.dos || doc.createdAt) === encounterDos
      );
      if (matchingDos.length === 1) {
        return matchingDos[0];
      }
      if (matchingDos.length > 1) {
        return this.pickPreferredDocumentRowByStatus(matchingDos, encounterDos);
      }
    }

    return this.pickPreferredDocumentRowByStatus(group, encounterDos);
  }

  private pickPreferredDocumentRowByStatus(
    group: PatientDoc[],
    encounterDos?: string | null
  ): PatientDoc {
    const pendingSignables = group.filter((doc) => {
      const d = doc as PatientDoc & { signableStatus?: string };
      return (
        this.isSignableDocumentRow(doc) && d.signableStatus !== "fully_signed"
      );
    });
    if (pendingSignables.length) {
      return this.preferEncounterDosThenNewest(pendingSignables, encounterDos);
    }

    const uploaded = group.filter((doc) => this.isUploadedDocumentRow(doc));
    if (uploaded.length) {
      return this.preferEncounterDosThenNewest(uploaded, encounterDos);
    }

    return this.preferEncounterDosThenNewest(group, encounterDos);
  }

  private preferEncounterDosThenNewest(
    group: PatientDoc[],
    encounterDos?: string | null
  ): PatientDoc {
    if (encounterDos) {
      const matchingDos = group.filter(
        (doc) => this.normalizeDosDate(doc.dos || doc.createdAt) === encounterDos
      );
      if (matchingDos.length) {
        return this.newestDocumentRow(matchingDos);
      }
    }
    return this.newestDocumentRow(group);
  }

  private newestDocumentRow(group: PatientDoc[]): PatientDoc {
    return group.reduce((best, current) => {
      const bestTs = this.documentTimestamp(best);
      const currentTs = this.documentTimestamp(current);
      return currentTs >= bestTs ? current : best;
    });
  }

  private documentTimestamp(doc: PatientDoc): number {
    const raw = doc.updatedAt || doc.createdAt;
    const ts = raw ? new Date(raw).getTime() : 0;
    return Number.isNaN(ts) ? 0 : ts;
  }

  /** Storage file key for OCR / S3 (not a PHI document row id when a real key exists). */
  documentOcrFileKey(doc: Partial<PatientDoc> | null | undefined): string {
    if (!doc) {
      return "";
    }
    const row = doc as PatientDoc & { key?: string };
    return (row.fileKey || row.key || "").trim();
  }

  /** S3 key, storage key, or document id accepted by `GET /api/v1/files/{key}`. */
  documentStreamKey(doc: Partial<PatientDoc> | null | undefined): string {
    if (!doc) {
      return "";
    }
    const row = doc as PatientDoc & { key?: string };
    return (row.fileKey || row.key || row.id || "").trim();
  }

  /** Resolve stream key for a document id (cache first, then id for PHI files lookup). */
  documentStreamKeyForId(
    documentId: string,
    docs?: PatientDoc[] | null,
  ): string {
    const id = documentId.trim();
    if (!id) {
      return "";
    }
    const cached = (docs ?? []).find((d) => d.id === id);
    const fromDoc = this.documentStreamKey(cached);
    return fromDoc || id;
  }

  getDocumentFileBlob(streamKeyOrDocumentId: string): Observable<Blob> {
    return this.getDocumentFileByKey(streamKeyOrDocumentId);
  }

  private resolveOcrFileKey(fileKeyUrl: string): string {
    const trimmed = String(fileKeyUrl ?? "").trim();
    if (!trimmed) {
      return "";
    }
    if (/s3\.amazonaws\.com/i.test(trimmed)) {
      const fromUrl = Utils.extractFileKeyFromUrl(trimmed);
      return (fromUrl || trimmed).split("?")[0].split("#")[0];
    }
    return trimmed.split("?")[0].split("#")[0];
  }
}
